<template>
  <AppLayout>
    <template #subheader>
      <nav
        class="w-full max-w-(--breakpoint-2xl) mx-auto pt-4 pb-2 px-4 dark:bg-gray-800 flex items-center justify-between z-20 flex-nowrap"
      >
        <TransactionsFilterComponent ref="transactionFilters" class="mr-2" @update:map-features="updateMapFeatures" />
        <TransactionsSearchComponent @update:map-features="updateMapFeatures" @search="clearSidebarFilters" @location-selected="handleLocationSelected" />
        <div class="flex items-center gap-2">
          <TransactionsSettingsComponent />
          <LayerManagerComponent />
          <!-- RED-310 — Analüütika drawer entry. Permission + feature flag
               gated; not rendered at all otherwise (no disabled button). -->
          <AnalyticsButton v-if="analyticsEnabled" />
        </div>
      </nav>
    </template>
    <div class="fixed w-full h-full">
      <div class="overflow-hidden">
        <TransactionsMapComponent
          ref="transactionsMap"
          :geom="objectGeom ? decodeURIComponent(objectGeom) : null"
          :brokerOverlayEnabled="brokerExportEnabled"
          :recentlyViewedEnabled="!objectId"
          @features-selected="handleFeaturesSelected"
        />
      </div>
      <TransactionsSidebarComponent :object-id="objectId" />
      <AnalyticsDrawer v-if="analyticsEnabled" />
      <MarkedTransactionsTableModal v-if="objectId" :object-id="objectId" />

      <!-- RED-236 — "Viimati vaadatud" (recently viewed) panel. Standalone
           Tehingute kaart only (no objectId), matching the broker button's
           scoping. Row click flies to the marker + re-opens the sidebar. -->
      <RecentlyViewedPanel v-if="!objectId" @select="handleRecentlyViewedSelect" />

      <!-- RED-180 — standalone broker .docx export. Lives only on
           /transactions without an objectId, and behind the `broker-export`
           feature flag (RED-242). Uses brokerSelectionStore which is fully
           isolated from the Hindamine markedTransactions selection (the two
           flows never share state). -->
      <template v-if="brokerExportEnabled">
        <div
          v-if="brokerSelectionStore.count >= 1"
          class="fixed bottom-3 left-1/2 -translate-x-1/2 z-30"
        >
          <Button
            severity="warn"
            size="small"
            data-testid="broker-export-open"
            @click="brokerDialogVisible = true"
          >
            <span class="pi pi-file-word mr-1.5 text-sm"></span>
            <span class="text-sm">{{ $t('messages.markedtransactions.broker-button-on-map') }}</span>
            <span class="ml-1.5 rounded-full bg-white/25 px-1.5 py-0.5 text-xs tabular-nums">{{ brokerSelectionStore.count }} / 5</span>
          </Button>
        </div>
        <BrokerExportDialog v-model:visible="brokerDialogVisible" />
      </template>
    </div>
  </AppLayout>
</template>

<script setup>
import axios from 'axios'
import Button from 'primevue/button'
import { usePage } from '@inertiajs/vue3'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import centroid from '@turf/centroid'
import { useBrokerSelectionStore } from '@/stores/brokerSelectionStore'
import { useTransactionStore } from '@/stores/transaction'
import { useFeature } from '@/composables/useFeature'
import BrokerExportDialog from '../components/transactions/BrokerExportDialog.vue'
import AnalyticsButton from '../components/transactions/analytics/AnalyticsButton.vue'
import AnalyticsDrawer from '../components/transactions/analytics/AnalyticsDrawer.vue'
import LayerManagerComponent from '../components/map/LayerManagerComponent.vue'
import MarkedTransactionsTableModal from '../components/transactions/MarkedTransactionsTableModal.vue'
import RecentlyViewedPanel from '../components/transactions/RecentlyViewedPanel.vue'
import TransactionsFilterComponent from '../components/transactions/TransactionsFilterComponent.vue'
import TransactionsMapComponent from '../components/transactions/TransactionsMapComponent.vue'
import TransactionsSearchComponent from '../components/transactions/TransactionsSearchComponent.vue'
import TransactionsSettingsComponent from '../components/transactions/TransactionsSettingsComponent.vue'
import TransactionsSidebarComponent from '../components/transactions/TransactionsSidebarComponent.vue'
import AppLayout from '../Layouts/AppLayout.vue'

const props = defineProps({
  objectId: Number,
  objectGeom: Number,
})

const { t } = useI18n()

const transactionsMap = ref(null)
const transactionFilters = ref(null)
const brokerDialogVisible = ref(false)

const brokerSelectionStore = useBrokerSelectionStore()

// RED-242 — the RED-180 broker export is gated behind the `broker-export`
// feature flag. It only exists on standalone /transactions (no objectId) AND
// when the flag is enabled for the current user (per-user override ?? global).
const brokerExport = useFeature('broker-export')
const brokerExportEnabled = computed(() => !props.objectId && brokerExport.value)

// RED-310 — Analüütika drawer: per-user permission `transactions-analytics`
// (Spatie, granted by the REdata admin) AND the Pennant flag of the same name.
// The server enforces both on every route; the Vue layer only hides.
const analyticsFeature = useFeature('transactions-analytics')
const analyticsEnabled = computed(
  () => !!usePage().props.auth?.can?.['transactions-analytics'] && analyticsFeature.value,
)

// RED-180 — drop the broker selection when the user leaves /transactions.
// Belt-and-suspenders with the `brokerOverlayEnabled` prop on the map:
// even if the store somehow gets repopulated in a different context, the
// next visit to /transactions starts from an empty selection.
onUnmounted(() => {
  brokerSelectionStore.clear()
})

const updateMapFeatures = () => {
  transactionsMap.value.map.addFeaturesToMap()
}

// Handler for feature selection event
const handleFeaturesSelected = ({ ids }) => {
  // console.log('Features selected in Transactions.vue:', ids)
  const transactionStore = useTransactionStore()
  // Call the action (to be created next)
  transactionStore.handleMapFeatureSelection(ids)
}

// RED-236 — re-open a transaction from the "Viimati vaadatud" panel: fly to the
// stored coordinate (if any), then open the right sidebar. The map records the
// view + draws the halo (gated to the standalone map via recentlyViewedEnabled).
const handleRecentlyViewedSelect = item => {
  const map = transactionsMap.value?.map
  if (item.coord && map) {
    // Centre the marker — and, from an overview zoom (fresh page load), zoom in
    // so it's not lost in a country-wide view. No zoom change when already
    // zoomed in (see focusCoordinate).
    map.focusCoordinate(item.coord)
  }
  useTransactionStore().handleListFeatureSelection([item.id])
}

const clearSidebarFilters = () => {
  transactionFilters.value.clearFilters()
}

const handleLocationSelected = (location) => {
  if (location.viitepunkt_x && location.viitepunkt_y) {
    const center = [location.viitepunkt_x, location.viitepunkt_y]
    transactionsMap.value.map.panTo(center)
    transactionsMap.value.map.zoomTo(12)
    transactionsMap.value.map.addSearchHighlight(location)
  }
}

</script>
